import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, useMediaQuery } from "@mui/material";

import Sidebar from "components/main/sidebar";
import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import { TalentApiProvider } from "components/Screens/Marketplace/Customer/TalentApiContext";
import { ShortlistProvider } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import { MeetingProvider } from "plg/meeting/MeetingContext";
import usePlgTalentApi from "plg/shell/usePlgTalentApi";
import usePlgMenuItems, { plgFooterItems } from "plg/shell/usePlgMenuItems";
import PlgTopBar from "plg/shell/PlgTopBar";
import PlgAccountMenu from "plg/shell/PlgAccountMenu";
import PlgMobileNav, { type PlgMobileNavProps } from "plg/shell/PlgMobileNav";
import { getPlgProfile } from "plg/auth/plgTokenStore";
import { identifyPlg } from "plg/analytics/identity";
import usePlgEntryTracking, { plgEntry } from "plg/analytics/usePlgEntryTracking";
import useMarkerWidget from "hooks/useMarkerWidget";
import { HIRE_ROUTES } from "plg/routes";
import "plg/shell/PlgLayout.scss";

// index.css pins body { overflow: hidden } for the portal shell; the PLG app scrolls the page itself
// (sticky headings and the Talent Network scroll restore rely on window scroll).
const BODY_SCROLL_CLASS = "plg-app-open";

// Prototype initials(): first letters of the first two words.
const orgInitials = (name: string) =>
  name
    .replace(/[^A-Za-z ]/g, " ")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const FOOTER_ITEMS = plgFooterItems();

type SidebarProps = Pick<PlgMobileNavProps, "menuItems" | "stickyItems" | "userInfo" | "onMenuItemClick">;

/** The menu, footer links and org card: the same in the sidebar and in the phone menu. */
function usePlgSidebarProps(): SidebarProps {
  const navigate = useNavigate();
  // Feedback opens the Marker.io widget (screenshot / screen recording), as the portal does. Not gated on the
  // portal's markerIntegration flag: PLG customers reach LaunchDarkly as anonymous visitors, so it reads off.
  // If the widget cannot load, the Feedback page instead.
  const { openMarkerWidget } = useMarkerWidget(true);
  const menuItems = usePlgMenuItems();
  const profile = getPlgProfile();
  const company = profile?.companyName || "";
  return {
    menuItems,
    stickyItems: FOOTER_ITEMS,
    userInfo: {
      name: company,
      companyName: company,
      email: profile?.email || "",
      // Prototype .sb-org: initials mark, company name, "Free account"
      avatar: <span className="sidebar-org-mark">{orgInitials(company)}</span>,
      subtitle: "Free account",
    },
    onMenuItemClick: (path: string, openInNewTab?: boolean) => {
      if (!path) return;
      if (path === HIRE_ROUTES.feedback) {
        openMarkerWidget(() => navigate(HIRE_ROUTES.feedback, { state: plgEntry("sidebar") }));
        return;
      }
      if (openInNewTab) window.open(path, "_blank", "noopener,noreferrer");
      else navigate(path, { state: plgEntry("sidebar") });
    },
  };
}

/** The top bar, the sidebar (and its phone drawer) and the page: inside the providers, for the menu's shortlist count. */
function PlgShell() {
  const [open, setOpen] = useState(() => window.innerWidth >= 1280);
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  const sidebar = usePlgSidebarProps();
  const { pathname } = useLocation();
  // Tablets (600–1024px): the open sidebar slides over the page instead of squeezing it, and closes on navigation.
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  const overlay = isTablet && open;
  useEffect(() => {
    if (isTablet) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return (
    <div className={`plg-shell disable-scrollbars${overlay ? " plg-shell--overlay" : ""}`}>
      <CssBaseline />
      <PlgTopBar open={open && !isTablet} onMenu={() => setPhoneNavOpen(true)} />
      {overlay && <div className="plg-shell__backdrop" aria-hidden="true" onClick={() => setOpen(false)} />}
      <Sidebar
        open={open}
        onToggle={setOpen}
        {...sidebar}
        logo={<EdgeLogo size="md" state="light" />}
        smallLogo={<EdgeLogo size="md" icon state="light" />}
      />
      <PlgMobileNav open={phoneNavOpen} onClose={() => setPhoneNavOpen(false)} {...sidebar} />
      <Box component="main" className="main-box plg-shell__main">
        <div className="plg-shell__drawer-header" />
        <Outlet />
      </Box>
    </div>
  );
}

function PlgLayout() {
  const renderAccountMenu = useCallback(() => <PlgAccountMenu />, []);
  const api = usePlgTalentApi(renderAccountMenu);
  const profile = getPlgProfile();
  useEffect(() => {
    if (profile) identifyPlg(profile);
    // Again when what the account traits carry changes (fetchPlgMe, a verification).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.plgCustomerId, profile?.isVerified, profile?.tier, profile?.emailType, profile?.industry, profile?.size, profile?.state]);
  usePlgEntryTracking();
  useEffect(() => {
    document.body.classList.add(BODY_SCROLL_CLASS);
    return () => document.body.classList.remove(BODY_SCROLL_CLASS);
  }, []);
  return (
    <TalentApiProvider value={api}>
      <ShortlistProvider>
        <MeetingProvider>
          <PlgShell />
        </MeetingProvider>
      </ShortlistProvider>
    </TalentApiProvider>
  );
}

export default PlgLayout;
